import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { apiClient } from '@/lib/apiClient';

/**
 * The dimension merge ladder, fetched from the engine that applies it
 * (GET /api/finance/dimension-set/precedence-ladder).
 *
 * Deliberately NOT a constant in this file: the precedence rules live in one place server-side
 * (DimensionPrecedence.CarrierOrder / .Ladder), and both surfaces that explain them — the plan-line
 * explain dialog and the dimension editor on master-data forms — read them from there. A hardcoded
 * copy here would silently drift the day someone reorders the carriers.
 *
 * Static metadata, so it is cached for the session.
 */

export interface DimensionPrecedenceRung {
  /** Sort key, 1 = strongest (wins). */
  rank: number;
  /** The tier label the specs and the UI speak in: "1", "2", "3", "4a"…"4d", "5". */
  step: string;
  /** CELL | HEADER_PIN | ENTITY_ROW_KEY | ENTITY_CARRIED_DEFAULT | ORG_UNIT_DEFAULT */
  source: string;
  /** PRODUCT_GROUP | CUSTOMER | TRADE_TYPE | SUPPLIER on a tier-4 rung; null otherwise. */
  carrier?: string | null;
  /** Localization key naming this source for an end user. */
  labelKey: string;
  /**
   * The DSL entity whose record supplies this rung ("ProductGroup", "TradeType", "OrgUnit"…); null
   * for rungs sourced from the cell or the header. Lets a master-data form recognise its own rung
   * without a name→rung map living here.
   */
  entity?: string | null;
}

/** The rungs a row entity can occupy — the sub-order that only matters when two of them collide. */
export const CARRIER_RUNG_SOURCE = 'ENTITY_CARRIED_DEFAULT';

/**
 * English fallbacks per label key. The keys are authored server-side (one source of truth for the
 * ORDER); the wording is a frontend concern, so it lives with the other UI strings — sv.json/en.json
 * override these, and an unknown key degrades to the key itself rather than blanking the UI.
 */
const RUNG_FALLBACKS: Record<string, string> = {
  'Finance.DimensionPrecedence.Cell': 'Value entered on the row',
  'Finance.DimensionPrecedence.HeaderPin': 'Pinned on the plan header',
  'Finance.DimensionPrecedence.RowEntity': 'The row itself (entity-backed axis)',
  'Finance.DimensionPrecedence.CarriedDefault.PRODUCT_GROUP': 'Default from the assortment',
  'Finance.DimensionPrecedence.CarriedDefault.CUSTOMER': 'Default from the customer',
  'Finance.DimensionPrecedence.CarriedDefault.TRADE_TYPE': 'Default from the trade type',
  'Finance.DimensionPrecedence.CarriedDefault.SUPPLIER': 'Default from the supplier',
  'Finance.DimensionPrecedence.OrgUnitDefault': 'Default from the organisational unit',
};

/** Human label for a rung, honouring the active language with an English fallback. */
export function rungLabel(t: TFunction, rung: DimensionPrecedenceRung): string {
  return t(rung.labelKey, { defaultValue: RUNG_FALLBACKS[rung.labelKey] ?? rung.labelKey });
}

export function useDimensionPrecedence() {
  return useQuery({
    queryKey: ['dimension-precedence-ladder'],
    queryFn: async () =>
      (await apiClient.get<DimensionPrecedenceRung[]>('/api/finance/dimension-set/precedence-ladder')).data,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * The carrier rungs in precedence order — what the dimension editor needs to say "if several
 * records on the same row set the same dimension, this is the order". Empty while loading, which
 * is what lets the caller hide the hint rather than render a half-truth.
 */
export function carrierRungs(ladder: DimensionPrecedenceRung[] | undefined): DimensionPrecedenceRung[] {
  return (ladder ?? [])
    .filter((r) => r.source === CARRIER_RUNG_SOURCE)
    .sort((a, b) => a.rank - b.rank);
}

/** Look up the rung a traced candidate sat on. */
export function rungByRank(
  ladder: DimensionPrecedenceRung[] | undefined,
  rank: number | undefined,
): DimensionPrecedenceRung | undefined {
  if (rank == null) return undefined;
  return (ladder ?? []).find((r) => r.rank === rank);
}
